/* The Print Doctor — artwork file check (client-side preflight).
   Reads the uploaded file in the browser and reports size vs the chosen product size, bleed,
   resolution at print size, color mode, fonts and page count. PDFs (and PDF-based .ai) are
   parsed with pdf.js from cdnjs, loaded only when needed. Nothing is uploaded here; the file
   still travels with the cart line as before. Uploaded as assets/pd-preflight.js by theme_patch.py. */
(function () {
  var PDFJS = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/6.3.289/pdf.min.mjs';
  var WORKER = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/6.3.289/pdf.worker.min.mjs';
  var BLEED = 0.125; // inches per side
  var libPromise = null;
  function pdfjs() {
    if (!libPromise) libPromise = import(PDFJS).then(function (m) { m.GlobalWorkerOptions.workerSrc = WORKER; return m; });
    return libPromise;
  }

  // ---------- helpers ----------
  function parseSize(txt) {
    if (!txt) return null;
    if (/\bft\b|'/.test(txt)) return null; // table covers etc. are quoted in feet
    var m = /(\d+(?:\.\d+)?)\s*(?:"|”|in(?:ch(?:es)?)?)?\s*[x×X]\s*(\d+(?:\.\d+)?)/.exec(txt);
    if (!m) return null;
    var w = +m[1], h = +m[2];
    if (!(w > 0 && h > 0)) return null;
    // "up to 24 x 24": custom-sized items with a maximum, not a fixed trim
    var max = /\bup\s*to\b|\bmax/i.test(txt);
    return { w: w, h: h, max: max, text: (max ? 'up to ' : '') + trim(w) + ' × ' + trim(h) + '"' };
  }
  function trim(n) { return (Math.round(n * 100) / 100).toString(); }
  function near(a, b, tol) { return Math.abs(a - b) <= (tol || 0.03); }
  function row(state, label, detail) { return { state: state, label: label, detail: detail }; }
  function readAscii(buf, n) {
    var u = new Uint8Array(buf, 0, Math.min(n, buf.byteLength));
    try { return new TextDecoder('latin1').decode(u); } catch (e) { var s = ''; for (var i = 0; i < u.length; i++) s += String.fromCharCode(u[i]); return s; }
  }

  // ---------- analysers: return "facts" about the file ----------
  // Image color spaces straight from the PDF source: pdf.js decodes images for the screen, so
  // the only way to know whether a placed photo is CMYK or RGB is to read its XObject dictionary.
  function pdfImageSpaces(buf) {
    var out = { RGB: 0, CMYK: 0, Gray: 0, other: 0 };
    try {
      var s = readAscii(buf, buf.byteLength);
      function obj(n) { var mm = new RegExp('(?:^|[^0-9])' + n + '\\s+0\\s+obj\\s*([\\s\\S]{0,400})').exec(s); return mm ? mm[1] : ''; }
      function space(cs) {
        if (!cs) return 'other';
        var ref = /^(\d+)\s+0\s+R/.exec(cs);
        if (ref) cs = obj(ref[1]);
        if (/\/Indexed/.test(cs)) { var base = /\/Indexed\s*(\/\w+|\d+\s+0\s+R|\[[^\]]*\])/.exec(cs); return base ? space(base[1]) : 'other'; }
        if (/\/ICCBased/.test(cs)) { var r = /\/ICCBased\s+(\d+)\s+0\s+R/.exec(cs); var d = r ? obj(r[1]) : cs; var n = /\/N\s+(\d)/.exec(d); return n ? ({ 1: 'Gray', 3: 'RGB', 4: 'CMYK' }[n[1]] || 'other') : 'other'; }
        if (/DeviceCMYK|CalCMYK/.test(cs)) return 'CMYK';
        if (/DeviceRGB|CalRGB|\/Lab\b/.test(cs)) return 'RGB';
        if (/DeviceGray|CalGray/.test(cs)) return 'Gray';
        if (/Separation|DeviceN/.test(cs)) return 'CMYK'; // spot/duotone: press-native
        return 'other';
      }
      var re = /\/Subtype\s*\/Image/g, mm;
      while ((mm = re.exec(s))) {
        // only this image's own dictionary: from its "N 0 obj" to its "stream"
        var start = s.lastIndexOf(' obj', mm.index), end = s.indexOf('stream', mm.index);
        if (start < 0 || start < mm.index - 2000) start = mm.index - 400;
        if (end < 0 || end > mm.index + 2000) end = mm.index + 400;
        var win = s.slice(Math.max(0, start), end);
        if (/\/ImageMask\s+true/.test(win)) continue; // 1-bit masks carry no color
        var cs = /\/ColorSpace\s*(\[[^\]]*\]|\/\w+|\d+\s+0\s+R)/.exec(win);
        out[space(cs ? cs[1] : '')]++;
      }
    } catch (e) {}
    return out;
  }
  async function analyzePdf(file) {
    var m = await pdfjs();
    var buf = await file.arrayBuffer();
    var imgSpaces = pdfImageSpaces(buf);
    var doc = await m.getDocument({ data: buf, isEvalSupported: false }).promise;
    var page = await doc.getPage(1);
    var v = page.view, uu = page.userUnit || 1;
    var wIn = (v[2] - v[0]) * uu / 72, hIn = (v[3] - v[1]) * uu / 72;
    if (page.rotate % 180 !== 0) { var t = wIn; wIn = hIn; hIn = t; }
    var ops = await page.getOperatorList();
    var fonts = {}, rgb = 0, cmyk = 0, images = 0, minDpi = Infinity;
    var ctm = [1, 0, 0, 1, 0, 0], stack = [];
    var O = m.OPS;
    for (var i = 0; i < ops.fnArray.length; i++) {
      var fn = ops.fnArray[i], a = ops.argsArray[i];
      if (fn === O.save) stack.push(ctm.slice());
      else if (fn === O.restore) ctm = stack.pop() || ctm;
      else if (fn === O.transform) ctm = m.Util.transform(ctm, a);
      else if (fn === O.setFont) fonts[a[0]] = true;
      else if (fn === O.setFillRGBColor || fn === O.setStrokeRGBColor) rgb++;
      else if (fn === O.setFillCMYKColor || fn === O.setStrokeCMYKColor) cmyk++;
      else if (fn === O.paintImageXObject || fn === O.paintInlineImageXObject || fn === O.paintImageXObjectRepeat) {
        var img = null;
        try { img = fn === O.paintInlineImageXObject ? a[0] : (page.objs.has(a[0]) ? page.objs.get(a[0]) : null); } catch (e) { img = null; }
        if (img && img.width) {
          images++;
          var dw = Math.hypot(ctm[0], ctm[1]) * uu / 72, dh = Math.hypot(ctm[2], ctm[3]) * uu / 72;
          if (dw > 0.05 && dh > 0.05) { var dpi = Math.min(img.width / dw, img.height / dh); if (dpi < minDpi) minDpi = dpi; }
        }
      }
    }
    var missing = [], embedded = 0;
    Object.keys(fonts).forEach(function (n) {
      try { var f = page.commonObjs.get(n); if (f) { if (f.missingFile) missing.push(f.name || n); else embedded++; } } catch (e) {}
    });
    // preview
    var canvas = document.createElement('canvas');
    try {
      var vp0 = page.getViewport({ scale: 1 }), scale = Math.min(360 / vp0.width, 260 / vp0.height, 2);
      var vp = page.getViewport({ scale: scale });
      canvas.width = Math.ceil(vp.width); canvas.height = Math.ceil(vp.height);
      await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
    } catch (e) { canvas = null; }
    var facts = { kind: 'PDF', pages: doc.numPages, wIn: wIn, hIn: hIn, images: images, minDpi: minDpi, rgb: rgb, cmyk: cmyk, imgSpaces: imgSpaces,
      fontsUsed: Object.keys(fonts).length, fontsMissing: missing, fontsEmbedded: embedded, preview: canvas };
    try { doc.destroy(); } catch (e) {}
    return facts;
  }

  function jpegInfo(buf) {
    var u = new Uint8Array(buf), i = 2, comps = 0;
    while (i < u.length - 9) {
      if (u[i] !== 0xFF) { i++; continue; }
      var marker = u[i + 1];
      if (marker === 0xD8 || marker === 0x01 || (marker >= 0xD0 && marker <= 0xD7)) { i += 2; continue; }
      var len = (u[i + 2] << 8) | u[i + 3];
      if ((marker >= 0xC0 && marker <= 0xCF) && marker !== 0xC4 && marker !== 0xC8 && marker !== 0xCC) { comps = u[i + 9]; break; }
      if (marker === 0xDA) break;
      i += 2 + len;
    }
    return { comps: comps };
  }
  function tiffInfo(buf) {
    var dv = new DataView(buf), le = dv.getUint16(0) === 0x4949, off = dv.getUint32(4, le), n = dv.getUint16(off, le), out = {};
    for (var i = 0; i < n; i++) {
      var e = off + 2 + i * 12, tag = dv.getUint16(e, le), type = dv.getUint16(e + 2, le);
      var val = type === 3 ? dv.getUint16(e + 8, le) : dv.getUint32(e + 8, le);
      if (tag === 256) out.w = val; else if (tag === 257) out.h = val; else if (tag === 262) out.photometric = val;
    }
    return out;
  }
  async function analyzeRaster(file) {
    var name = file.name.toLowerCase(), buf = await file.arrayBuffer();
    var facts = { kind: 'image', pages: 1, color: 'RGB', pxW: 0, pxH: 0, preview: null };
    if (/\.(tif|tiff)$/.test(name)) {
      var t = tiffInfo(buf); facts.kind = 'TIFF'; facts.pxW = t.w || 0; facts.pxH = t.h || 0;
      facts.color = t.photometric === 5 ? 'CMYK' : (t.photometric <= 1 ? 'grayscale' : 'RGB');
      return facts;
    }
    if (/\.(jpe?g)$/.test(name)) { var j = jpegInfo(buf); facts.kind = 'JPEG'; facts.color = j.comps === 4 ? 'CMYK' : (j.comps === 1 ? 'grayscale' : 'RGB'); }
    else if (/\.png$/.test(name)) facts.kind = 'PNG';
    var url = URL.createObjectURL(new Blob([buf], { type: file.type || 'image/*' }));
    try {
      var img = await new Promise(function (res, rej) { var im = new Image(); im.onload = function () { res(im); }; im.onerror = rej; im.src = url; });
      facts.pxW = img.naturalWidth; facts.pxH = img.naturalHeight; facts.preview = img;
    } catch (e) { /* browser cannot decode (CMYK JPEG in some browsers) */ }
    return facts;
  }
  async function analyzeVectorText(file) {
    var buf = await file.arrayBuffer(), head = readAscii(buf, 4000), name = file.name.toLowerCase();
    if (/^%PDF/.test(head)) return analyzePdf(file);
    var facts = { kind: /\.svg$/.test(name) ? 'SVG' : (/\.ai$/.test(name) ? 'Illustrator' : 'EPS'), pages: 1, vector: true, preview: null };
    var bb = /%%HiResBoundingBox:\s*([\d.\-]+)\s+([\d.\-]+)\s+([\d.\-]+)\s+([\d.\-]+)/.exec(head) || /%%BoundingBox:\s*([\d.\-]+)\s+([\d.\-]+)\s+([\d.\-]+)\s+([\d.\-]+)/.exec(head);
    if (bb) { facts.wIn = (+bb[3] - +bb[1]) / 72; facts.hIn = (+bb[4] - +bb[2]) / 72; }
    if (facts.kind === 'SVG') {
      var full = readAscii(buf, 200000), w = /<svg[^>]*\swidth="([\d.]+)(in|mm|cm|pt|px)?"/.exec(full), h = /<svg[^>]*\sheight="([\d.]+)(in|mm|cm|pt|px)?"/.exec(full);
      var toIn = { in: 1, mm: 1 / 25.4, cm: 1 / 2.54, pt: 1 / 72, px: 1 / 96 };
      if (w && h && w[2] && h[2] && w[2] !== 'px') { facts.wIn = +w[1] * toIn[w[2]]; facts.hIn = +h[1] * toIn[h[2]]; }
      var url = URL.createObjectURL(new Blob([buf], { type: 'image/svg+xml' }));
      try { facts.preview = await new Promise(function (res, rej) { var im = new Image(); im.onload = function () { res(im); }; im.onerror = rej; im.src = url; }); } catch (e) {}
      if (/\brgb\(|#[0-9a-f]{3,6}\b/i.test(full)) facts.rgb = 1;
    }
    return facts;
  }
  function analyze(file) {
    var n = file.name.toLowerCase();
    if (/\.pdf$/.test(n)) return analyzePdf(file);
    if (/\.(png|jpe?g|tif|tiff|webp)$/.test(n)) return analyzeRaster(file);
    if (/\.(ai|eps|svg)$/.test(n)) return analyzeVectorText(file);
    if (/\.psd$/.test(n)) return Promise.resolve({ kind: 'Photoshop', pages: 1, opaque: true });
    return Promise.resolve({ kind: 'file', opaque: true });
  }

  // ---------- verdicts ----------
  function sizeRows(f, size) {
    if (!size) return [row('info', 'Size', 'Pick a size above and we check the file against it.')];
    if (f.pxW && !f.wIn) return null; // raster: handled by the resolution row
    if (!f.wIn) return [row('info', 'Size', 'This file type does not carry a page size we can read. We check it by hand before printing.')];
    var W = f.wIn, H = f.hIn, s = size, dims = trim(W) + ' × ' + trim(H) + '"';
    var b2 = BLEED * 2;
    if (s.max) { // custom-sized piece: we cut to the file, so it only has to fit the maximum
      var fits = (W <= s.w + b2 + 0.03 && H <= s.h + b2 + 0.03) || (W <= s.h + b2 + 0.03 && H <= s.w + b2 + 0.03);
      if (fits) return [row('ok', 'Size', dims + '. We cut to your file, which fits the ' + trim(s.w) + ' × ' + trim(s.h) + '" maximum. Include 1/8" bleed on every side if color runs to the edge.')];
      return [row('bad', 'Size', dims + ' is larger than the ' + trim(s.w) + ' × ' + trim(s.h) + '" maximum for this item. Resize the artwork.')];
    }
    if (near(W, s.w + b2) && near(H, s.h + b2)) return [row('ok', 'Size', dims + '. That is ' + s.text + ' with 1/8" bleed on every side.')];
    if (near(W, s.h + b2) && near(H, s.w + b2)) return [row('ok', 'Size', dims + ', rotated. ' + s.text + ' with bleed. We turn it for you.')];
    if (near(W, s.w) && near(H, s.h) || near(W, s.h) && near(H, s.w))
      return [row('warn', 'Bleed', dims + ' is exactly ' + s.text + ' with no bleed. Fine if nothing needs to reach the edge. Otherwise add 1/8" on every side so trimming never leaves a white sliver.')];
    var r = W / H, want = s.w / s.h;
    if (near(r, want, 0.01) || near(r, 1 / want, 0.01))
      return [row('warn', 'Size', dims + '. Same proportions as ' + s.text + ', so it scales cleanly, but there is no bleed. Sending it at final size plus 1/8" bleed is safer.')];
    return [row('bad', 'Size', dims + ' does not match ' + s.text + '. It would be cropped or stretched. Resize the artwork, or pick the size that matches it.')];
  }
  function dpiRows(f, size) {
    var large = size && Math.max(size.w, size.h) >= 18; // signs, banners, posters: read from a distance
    var good = large ? 100 : 300, okish = large ? 60 : 150;
    var note = large ? ' Big signs are viewed from a distance, so ' + good + ' dpi at full size prints crisp.' : ' Small pieces are read up close and need ' + good + ' dpi at full size.';
    if (f.pxW) {
      if (!size) return [row('info', 'Resolution', f.pxW + ' × ' + f.pxH + ' px. Pick a size above to see the print resolution.')];
      if (size.max) { // custom-sized: report how big it can print before it goes soft
        var okW = f.pxW / good, okH = f.pxH / good, fitsMax = okW >= size.w && okH >= size.h;
        var msg2 = f.pxW + ' × ' + f.pxH + ' px prints crisp up to ' + trim(Math.min(okW, size.w)) + ' × ' + trim(Math.min(okH, size.h)) + '".';
        return [row(fitsMax ? 'ok' : 'info', 'Resolution', msg2 + (fitsMax ? '' : ' Bigger than that goes soft; send a larger image or a vector file if you want the full ' + trim(size.w) + ' × ' + trim(size.h) + '".'))];
      }
      var ratio = f.pxW / f.pxH, rot = Math.abs(ratio - size.h / size.w) < Math.abs(ratio - size.w / size.h);
      var dpi = rot ? Math.min(f.pxW / size.h, f.pxH / size.w) : Math.min(f.pxW / size.w, f.pxH / size.h);
      var px = f.pxW + ' × ' + f.pxH + ' px = ' + Math.round(dpi) + ' dpi at ' + size.text + '.';
      if (dpi >= good) return [row('ok', 'Resolution', px)];
      if (dpi >= okish) return [row('warn', 'Resolution', px + ' Expect some softness up close.' + note)];
      return [row('bad', 'Resolution', px + ' That will print blurry or blocky.' + note + ' Send a larger image or a vector file.')];
    }
    if (f.kind === 'PDF' || f.vector) {
      if (!f.images) return [row('ok', 'Resolution', f.vector && f.kind !== 'PDF' ? 'Vector artwork. Sharp at any size.' : 'No placed images. Vector text and shapes stay sharp at any size.')];
      if (!isFinite(f.minDpi)) return [row('info', 'Resolution', f.images + ' placed image' + (f.images > 1 ? 's' : '') + '. We could not measure the effective resolution; we check it by hand.')];
      var d = Math.round(f.minDpi), msg = 'Lowest placed image is ' + d + ' dpi at the size it is used.';
      if (d >= good) return [row('ok', 'Resolution', msg)];
      if (d >= okish) return [row('warn', 'Resolution', msg + ' Expect some softness up close.' + note)];
      return [row('bad', 'Resolution', msg + ' That image will print blurry.' + note)];
    }
    return [];
  }
  function colorRows(f) {
    if (f.color) {
      if (f.color === 'CMYK') return [row('ok', 'Color', 'CMYK. What you see is close to what prints.')];
      if (f.color === 'grayscale') return [row('ok', 'Color', 'Grayscale.')];
      return [row('warn', 'Color', 'RGB. We convert it to CMYK for press, so bright blues, greens and oranges come out a little duller. Export as CMYK if the exact shade matters.')];
    }
    if (f.kind === 'PDF' || f.kind === 'SVG') {
      var im = f.imgSpaces || { RGB: 0, CMYK: 0, Gray: 0, other: 0 };
      var imgTotal = im.RGB + im.CMYK + im.Gray + im.other;
      var rgbAny = f.rgb > 0 || im.RGB > 0, cmykAny = f.cmyk > 0 || im.CMYK > 0 || im.Gray > 0;
      var parts = [];
      if (f.rgb || f.cmyk) parts.push('vector art ' + (f.rgb && f.cmyk ? 'mixed RGB and CMYK' : f.rgb ? 'RGB' : 'CMYK'));
      if (imgTotal) {
        var bits = [];
        if (im.CMYK) bits.push(im.CMYK + ' CMYK'); if (im.RGB) bits.push(im.RGB + ' RGB'); if (im.Gray) bits.push(im.Gray + ' grayscale'); if (im.other) bits.push(im.other + ' unknown');
        parts.push(imgTotal + ' placed image' + (imgTotal > 1 ? 's' : '') + ' (' + bits.join(', ') + ')');
      }
      var what = parts.join('; ') + '.';
      if (rgbAny && !cmykAny) return [row('warn', 'Color', what + ' RGB is converted to CMYK for press, so bright blues, greens and oranges come out a little duller. Export as CMYK if the exact shade matters.')];
      if (rgbAny && cmykAny) return [row('warn', 'Color', what + ' The RGB parts shift slightly in print; the CMYK parts print as shown.')];
      if (cmykAny) return [row('ok', 'Color', what + ' Press-ready.')];
      if (imgTotal && im.other === imgTotal) return [row('info', 'Color', what + ' We could not read the color mode of these images; prepress converts to CMYK if needed.')];
      return [row('info', 'Color', 'No color content found to check.')];
    }
    return [];
  }
  function fontRows(f) {
    if (f.kind !== 'PDF') return [];
    if (!f.fontsUsed) return [row('ok', 'Fonts', 'No live text. Everything is outlined or image.')];
    if (f.fontsMissing.length) return [row('bad', 'Fonts', 'Not embedded: ' + f.fontsMissing.slice(0, 3).join(', ') + (f.fontsMissing.length > 3 ? ' and more' : '') + '. They would print in a substitute typeface. Re-export with fonts embedded, or convert text to outlines.')];
    return [row('ok', 'Fonts', f.fontsEmbedded + ' font' + (f.fontsEmbedded > 1 ? 's' : '') + ' embedded.')];
  }
  function pageRows(f, ctx) {
    var two = ctx.sides === 2, back = ctx.side === 'back';
    if (!f.pages || f.pages < 2) {
      if (two && !back && f.kind === 'PDF') return [row('info', 'Pages', 'One page. Add the back as a second file below, or send one PDF with both sides as two pages.')];
      return [];
    }
    if (back) return [row('warn', 'Pages', f.pages + ' pages in the back file. We print page 1 as the back.')];
    if (f.pages === 2 && two) return [row('ok', 'Pages', '2 pages. Page 1 is the front, page 2 the back. No separate back file needed.')];
    return [row('warn', 'Pages', f.pages + ' pages. We print page 1' + (two ? ' as the front and page 2 as the back' : '') + '. Send one file per design if these are different pieces.')];
  }

  function verdicts(f, ctx) {
    var size = parseSize(ctx.size);
    var rows = [row('ok', 'File', f.kind + (f.pages > 1 ? ', ' + f.pages + ' pages' : '') + '.')];
    if (f.opaque) { rows.push(row('info', 'Check', 'We cannot open this type in the browser. Our prepress check happens before it prints.')); return rows; }
    var sr = sizeRows(f, size); if (sr) rows = rows.concat(sr);
    rows = rows.concat(dpiRows(f, size), colorRows(f), fontRows(f), pageRows(f, ctx));
    return rows;
  }
  function summary(rows) {
    var mark = { ok: '✓', warn: '!', bad: '✗', info: '·' };
    return rows.map(function (r) {
      var d = r.detail.split(/\.(?:\s|$)/)[0]; return r.label + ' ' + mark[r.state] + ' ' + d;
    }).join(' | ').slice(0, 250);
  }

  // ---------- rendering ----------
  var ICON = {
    ok: '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 10.5l4 4 8-9"/></svg>',
    warn: '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M10 4v7"/><circle cx="10" cy="15" r="1" fill="currentColor"/></svg>',
    bad: '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M5 5l10 10M15 5L5 15"/></svg>',
    info: '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M10 9v6"/><circle cx="10" cy="5.5" r="1" fill="currentColor"/></svg>'
  };
  function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
  var money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
  // ---------- press proof: the customer's file on a sheet at the ordered size ----------
  // trim (solid), bleed (cyan dashes, 1/8" out), safe area (magenta dashes, 1/8" in), crop marks,
  // dimensions, and a US Letter outline (or a 1-inch rule for small pieces) so the size is felt.
  function proofSheet(facts, size, ctx) {
    var art = facts.preview; if (!art || !size) return null;
    if (art.tagName === 'IMG' && !(art.complete && art.naturalWidth)) return null;
    var aw = art.tagName === 'CANVAS' ? art.width : art.naturalWidth, ah = art.tagName === 'CANVAS' ? art.height : art.naturalHeight;
    if (!aw || !ah) return null;
    // the piece: a fixed trim, or (custom-cut "up to") the file's own size capped to the maximum
    var fileW = facts.wIn, fileH = facts.hIn;
    var trimW = size.w, trimH = size.h;
    if (size.max) {
      if (fileW && fileH) { trimW = Math.min(fileW, size.w); trimH = Math.min(fileH, size.h); }
      else { var r0 = aw / ah; trimW = Math.min(size.w, size.h * r0); trimH = trimW / r0; }
    }
    // rotate the art when its orientation is the other way round
    var rot = (aw > ah) !== (trimW > trimH) && Math.abs(trimW - trimH) > 0.01;
    // the art's own size in inches: PDF pages know it, rasters are assumed to print at trim (+bleed if the ratio says so)
    var artW, artH;
    if (fileW && fileH && !size.max) { artW = rot ? fileH : fileW; artH = rot ? fileW : fileH; }
    else { var rr = rot ? ah / aw : aw / ah, tb = (trimW + 2 * BLEED) / (trimH + 2 * BLEED); artW = near(rr, tb, 0.02) ? trimW + 2 * BLEED : trimW; artH = artW / rr; if (artH < trimH - 0.01) { artH = trimH; artW = artH * rr; } }
    var refLetter = Math.max(trimW, trimH) >= 6;
    var padIn = 0.6, extraW = refLetter ? Math.max(0, 8.5 - trimW) : 0, extraH = refLetter ? Math.max(0, 11 - trimH) : 0;
    var sheetW = Math.max(artW, trimW + 2 * BLEED) + extraW + 2 * padIn, sheetH = Math.max(artH, trimH + 2 * BLEED) + extraH + 2 * padIn + 0.5;
    var cssW = 560, scale = cssW / sheetW, cssH = Math.round(sheetH * scale);
    if (cssH > 440) { scale = 440 / sheetH; cssH = 440; cssW = Math.round(sheetW * scale); }
    var dpr = Math.min(2, window.devicePixelRatio || 1);
    var c = document.createElement('canvas'); c.width = Math.round(cssW * dpr); c.height = Math.round(cssH * dpr); c.style.width = '100%';
    var g = c.getContext('2d'); g.scale(dpr, dpr);
    var S = function (inches) { return inches * scale; };
    // sheet
    g.fillStyle = '#F5F4F0'; g.fillRect(0, 0, cssW, cssH);
    g.fillStyle = 'rgba(28,27,25,.045)'; for (var i = 0; i < 900; i++) { g.fillRect(Math.random() * cssW, Math.random() * cssH, 1, 1); }
    // origin: the trim's top-left; art is centred on the trim
    var ox = S(padIn + Math.max(0, (artW - trimW) / 2, BLEED)), oy = S(padIn + 0.5 + Math.max(0, (artH - trimH) / 2, BLEED));
    var tw = S(trimW), th = S(trimH);
    var ax = ox - S((artW - trimW) / 2), ay = oy - S((artH - trimH) / 2), awp = S(artW), ahp = S(artH);
    g.save(); g.shadowColor = 'rgba(28,27,25,.25)'; g.shadowBlur = 14; g.shadowOffsetY = 4; g.fillStyle = '#fff'; g.fillRect(ax, ay, awp, ahp); g.restore();
    g.save();
    if (rot) { g.translate(ax + awp / 2, ay + ahp / 2); g.rotate(Math.PI / 2); g.drawImage(art, -ahp / 2, -awp / 2, ahp, awp); }
    else g.drawImage(art, ax, ay, awp, ahp);
    g.restore();
    // bleed, trim, safe
    var b = S(BLEED);
    g.setLineDash([5, 4]); g.lineWidth = 1.2;
    g.strokeStyle = '#00AEEF'; g.strokeRect(ox - b, oy - b, tw + 2 * b, th + 2 * b);
    g.strokeStyle = '#EC008C'; g.strokeRect(ox + b, oy + b, tw - 2 * b, th - 2 * b);
    g.setLineDash([]); g.strokeStyle = '#1C1B19'; g.lineWidth = 1.4; g.strokeRect(ox, oy, tw, th);
    // crop marks
    var gap = S(0.12), len = S(0.3); g.lineWidth = 1; g.strokeStyle = '#1C1B19';
    [[ox, oy, -1, -1], [ox + tw, oy, 1, -1], [ox, oy + th, -1, 1], [ox + tw, oy + th, 1, 1]].forEach(function (p) {
      g.beginPath(); g.moveTo(p[0] + p[2] * gap, p[1]); g.lineTo(p[0] + p[2] * (gap + len), p[1]); g.moveTo(p[0], p[1] + p[3] * gap); g.lineTo(p[0], p[1] + p[3] * (gap + len)); g.stroke();
    });
    // registration mark
    var rx = ox + tw + S(0.55), ry = oy - S(0.42);
    if (rx + 8 < cssW) { g.beginPath(); g.arc(rx, ry, 5, 0, Math.PI * 2); g.stroke(); g.beginPath(); g.moveTo(rx - 8, ry); g.lineTo(rx + 8, ry); g.moveTo(rx, ry - 8); g.lineTo(rx, ry + 8); g.stroke(); g.beginPath(); g.arc(rx, ry, 1.6, 0, Math.PI * 2); g.fillStyle = '#1C1B19'; g.fill(); }
    // dimensions
    g.fillStyle = '#1C1B19'; g.font = '600 11px Inter, system-ui, sans-serif'; g.textAlign = 'center'; g.textBaseline = 'bottom';
    var dy = oy - b - S(0.22);
    g.beginPath(); g.moveTo(ox, dy); g.lineTo(ox + tw, dy); g.stroke();
    g.beginPath(); g.moveTo(ox, dy - 4); g.lineTo(ox, dy + 4); g.moveTo(ox + tw, dy - 4); g.lineTo(ox + tw, dy + 4); g.stroke();
    g.fillText(trim(trimW) + ' in', ox + tw / 2, dy - 3);
    g.save(); g.translate(ox + tw + b + S(0.22), oy + th / 2); g.rotate(-Math.PI / 2); g.textBaseline = 'bottom';
    g.beginPath(); g.moveTo(-th / 2, 0); g.lineTo(th / 2, 0); g.stroke(); g.beginPath(); g.moveTo(-th / 2, -4); g.lineTo(-th / 2, 4); g.moveTo(th / 2, -4); g.lineTo(th / 2, 4); g.stroke();
    g.fillText(trim(trimH) + ' in', 0, -3); g.restore();
    // scale reference
    g.textAlign = 'left'; g.textBaseline = 'top'; g.font = '500 10px Inter, system-ui, sans-serif'; g.fillStyle = '#6b6862';
    if (refLetter) {
      // drawn over the artwork, so a pale stroke under a dark dashed one stays legible on any colour
      var lx = ox, ly = oy + th - S(11); if (ly < S(0.3)) ly = oy;
      g.setLineDash([]); g.strokeStyle = 'rgba(255,255,255,.55)'; g.lineWidth = 3; g.strokeRect(lx, ly, S(8.5), S(11));
      g.setLineDash([3, 3]); g.strokeStyle = 'rgba(28,27,25,.55)'; g.lineWidth = 1; g.strokeRect(lx, ly, S(8.5), S(11)); g.setLineDash([]);
      var cap = 'US Letter sheet for scale', cx = lx + 6, cy = Math.min(ly + 6, cssH - 14);
      g.fillStyle = 'rgba(255,255,255,.85)'; g.fillRect(cx - 3, cy - 2, g.measureText(cap).width + 6, 14);
      g.fillStyle = '#1C1B19'; g.fillText(cap, cx, cy);
    } else {
      var ux = ox, uy = oy + th + b + S(0.28); if (uy + 14 > cssH) uy = cssH - 14;
      g.strokeStyle = '#1C1B19'; g.lineWidth = 1; g.beginPath(); g.moveTo(ux, uy); g.lineTo(ux + S(1), uy); g.stroke();
      for (var t = 0; t <= 4; t++) { var tx = ux + S(t / 4); g.beginPath(); g.moveTo(tx, uy); g.lineTo(tx, uy - (t % 4 === 0 ? 6 : 3)); g.stroke(); }
      g.fillText('1 in', ux + S(1) + 5, uy - 6);
    }
    // legend
    g.font = '600 10px Inter, system-ui, sans-serif'; g.textAlign = 'left'; g.textBaseline = 'top';
    var lgx = S(padIn), lgy = 8;
    g.fillStyle = '#1C1B19'; g.fillText('PROOF', lgx, lgy);
    g.fillStyle = '#6b6862'; g.font = '500 10px Inter, system-ui, sans-serif';
    g.fillText(size.text + (ctx.sides === 2 ? (ctx.side === 'back' ? ' · back' : ' · front') : '') + (rot ? ' · turned to fit' : ''), lgx + 44, lgy);
    var kx = cssW - S(padIn) - 190; if (kx > lgx + 200) {
      g.fillStyle = '#00AEEF'; g.fillRect(kx, lgy + 3, 10, 2); g.fillStyle = '#6b6862'; g.fillText('bleed', kx + 14, lgy);
      g.fillStyle = '#1C1B19'; g.fillRect(kx + 52, lgy + 3, 10, 2); g.fillStyle = '#6b6862'; g.fillText('trim', kx + 66, lgy);
      g.fillStyle = '#EC008C'; g.fillRect(kx + 98, lgy + 3, 10, 2); g.fillStyle = '#6b6862'; g.fillText('safe area', kx + 112, lgy);
    }
    return c;
  }

  function render(panel, rows, facts, ctx) {
    var worst = rows.some(function (r) { return r.state === 'bad'; }) ? 'bad' : rows.some(function (r) { return r.state === 'warn'; }) ? 'warn' : 'ok';
    var side = ctx.sides === 2 ? (ctx.side === 'back' ? ' · back' : ' · front') : '';
    var head = { ok: 'Looks good to print', warn: 'Printable, with notes', bad: 'Needs attention before printing' }[worst];
    var artEl = document.querySelector('[data-pd-artwork]'), doc = artEl && artEl.getAttribute('data-pd-mascot');
    var html = '<div class="pd-check__head pd-check__head--' + worst + '">' + (doc ? '<img class="pd-check__doc" src="' + esc(doc) + '" alt="" width="34" height="34">' : '') + '<span class="pd-check__icon">' + ICON[worst] + '</span><b>File check' + side + '</b><span class="pd-check__verdict">' + head + '</span></div>';
    var size = parseSize(ctx.size), proof = null;
    try { proof = facts.opaque ? null : proofSheet(facts, size, ctx); } catch (e) { proof = null; }
    if (proof) html += '<div class="pd-check__proof" data-pd-proof><div class="pd-check__proof-cap"><b>Press proof</b><span>' + (worst === 'bad' ? 'Fix the file or approve it below.' : 'Adding it to the cart approves this proof.') + '</span></div></div>';
    html += '<div class="pd-check__body"><ul class="pd-check__rows">';
    rows.forEach(function (r) { html += '<li class="pd-check__row pd-check__row--' + r.state + '"><span class="pd-check__icon">' + ICON[r.state] + '</span><span><b>' + esc(r.label) + '</b> ' + esc(r.detail) + '</span></li>'; });
    html += '</ul><div class="pd-check__preview" data-pd-preview></div></div>';
    if (worst === 'bad' || (worst === 'warn' && ctx.fix)) {
      html += '<div class="pd-check__choices">';
      if (worst === 'bad') html += '<label class="pd-check__ack"><input type="checkbox" data-pd-ack> I understand the warnings. Approve the proof and print it as it is.</label>';
      if (ctx.fix) html += '<label class="pd-check__ack pd-check__ack--fix"><input type="checkbox" data-pd-fix> <span>Have us fix it for ' + money.format(ctx.fix / 100)
        + '<small>Basic fixes only: resizing to the ordered size, adding bleed, RGB to CMYK, outlining fonts. Not a redesign. If the file needs more than that we may decline, refund this fee and ask for our '
        + (ctx.design ? money.format(ctx.design / 100) + ' ' : '') + 'design service instead.</small></span></label>';
      html += '</div>';
    }
    panel.innerHTML = html;
    panel.dataset.state = worst;
    var pv = panel.querySelector('[data-pd-preview]');
    if (proof) { panel.querySelector('[data-pd-proof]').appendChild(proof); pv.remove(); }
    else if (facts.preview) { if (facts.preview.tagName === 'CANVAS') pv.appendChild(facts.preview); else { var im = document.createElement('img'); im.src = facts.preview.src; im.alt = 'Preview of your file'; pv.appendChild(im); } }
    else pv.remove();
    return worst;
  }
  function renderBusy(panel) { panel.innerHTML = '<div class="pd-check__head pd-check__head--busy"><span class="pd-check__spin"></span><b>File check</b><span class="pd-check__verdict">Reading your file…</span></div>'; panel.dataset.state = 'busy'; }
  function renderFail(panel, e) { panel.innerHTML = '<div class="pd-check__head pd-check__head--info"><span class="pd-check__icon">' + ICON.info + '</span><b>File check</b><span class="pd-check__verdict">Could not read this file in the browser</span></div><p class="pd-check__note">It still goes with your order and our prepress check happens before it prints.</p>'; panel.dataset.state = 'info'; }

  // ---------- public API ----------
  var cache = []; // [{file, facts}] for the last few files (front + back)
  function cached(file) { for (var i = 0; i < cache.length; i++) if (cache[i].file === file) return cache[i].facts; return null; }
  window.pdPreflight = {
    parseSize: parseSize,
    /** run(file, ctx:{size, sides, side:'front'|'back', fix: cents, design: cents}, panel) -> Promise<{state, summary, pages}> */
    run: function (file, ctx, panel) {
      ctx = ctx || {};
      panel.hidden = false;
      var known = cached(file);
      var p = known ? Promise.resolve(known) : (renderBusy(panel), analyze(file).then(function (f) { cache.unshift({ file: file, facts: f }); cache = cache.slice(0, 4); return f; }));
      return p.then(function (facts) {
        var rows = verdicts(facts, ctx);
        var worst = render(panel, rows, facts, ctx);
        return { state: worst, summary: summary(rows), pages: facts.pages || 1 };
      }).catch(function (e) { renderFail(panel, e); return { state: 'info', summary: 'File check: not readable in browser', pages: 1 }; });
    },
    clear: function (panel) { panel.hidden = true; panel.innerHTML = ''; panel.dataset.state = ''; }
  };
})();
